import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, Form, Input } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import EditIcon from '@/assets/edit.svg?react'
import type { DisplayInfo, Localization, Runtime } from '@/types'
import {
  MAX_APP_CATEGORIES,
  PRESET_APP_CATEGORIES,
  contentLangLabel,
} from '../languages'
import { ensureLocalization } from './formModel'
import SupportedLanguagesSelect from './SupportedLanguagesSelect'
import AppTagsSelect from './AppTagsSelect'
import ScreenshotUploader from './ScreenshotUploader'
import SupportAccountField from './SupportAccountField'
import { DISPLAY_URL_MAX, isOptionalHttpsUrl } from './httpsUrl'

/** 联系客服账号暂不展示。组件、表单字段和提交数据都保留，放开时改为 true。 */
const SHOW_SUPPORT_ACCOUNT = false

/** 清单未写 runtimes 时接口默认 desktop，这里同样只显示 PC 截图。web 对应移动端截图。 */
export function screenshotVisibility(runtimes: Runtime[] | undefined): {
  hasPc: boolean
  hasMobile: boolean
} {
  const list = runtimes?.length ? runtimes : (['desktop'] as Runtime[])
  return {
    hasPc: list.includes('desktop'),
    hasMobile: list.includes('web'),
  }
}

export default function DisplayInfoSection({
  value,
  editable,
  app_id,
  version_id,
  runtimes,
  onChange,
}: {
  value: DisplayInfo
  editable: boolean
  app_id: string
  version_id: string
  /** 详情 main_package.runtimes */
  runtimes?: Runtime[]
  onChange: (next: DisplayInfo) => void
}) {
  const { t } = useTranslation()
  const [activeLang, setActiveLang] = useState(value.default_language)
  const [catOpen, setCatOpen] = useState(false)
  const valueRef = useRef(value)
  valueRef.current = value

  // 切换版本或默认语言变化时，回到对应语言 Tab，避免仍停在空的 English
  useEffect(() => {
    setActiveLang(value.default_language)
  }, [version_id, value.default_language])

  // 截图按清单 runtimes 显隐，与是否分平台子包无关。
  const { hasPc, hasMobile } = screenshotVisibility(runtimes)

  const lang = value.supported_languages.includes(activeLang)
    ? activeLang
    : value.default_language
  const loc: Localization = value.localizations[lang] ?? {
    tagline: '',
    description: '',
    pc_screenshots: [],
    mobile_screenshots: [],
  }

  const patchLoc = (patch: Partial<Localization>) => {
    const ensured = ensureLocalization(value, lang)
    onChange({
      ...ensured,
      localizations: {
        ...ensured.localizations,
        [lang]: { ...ensured.localizations[lang], ...patch },
      },
    })
  }

  const onLangsChange = (langs: string[], def: string) => {
    onChange({
      ...value,
      supported_languages: langs,
      default_language: def,
    })
    if (!langs.includes(activeLang)) setActiveLang(def)
  }

  const removeLang = (code: string) => {
    if (code === value.default_language) return
    onLangsChange(
      value.supported_languages.filter((c) => c !== code),
      value.default_language,
    )
  }

  const toggleCategory = (cat: string) => {
    if (value.categories.includes(cat)) {
      onChange({ ...value, categories: value.categories.filter((c) => c !== cat) })
      return
    }
    if (value.categories.length >= MAX_APP_CATEGORIES) return
    onChange({ ...value, categories: [...value.categories, cat] })
  }

  const catFull = value.categories.length >= MAX_APP_CATEGORIES

  const catPanel = (
    <div
      style={{
        width: 280,
        maxHeight: 320,
        overflow: 'auto',
        background: '#fff',
        border: '1px solid var(--color-border-line)',
        borderRadius: 12,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        padding: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {PRESET_APP_CATEGORIES.map((cat) => {
        const checked = value.categories.includes(cat)
        const blocked = catFull && !checked
        return (
          <button
            key={cat}
            type="button"
            disabled={!editable || blocked}
            onClick={() => toggleCategory(cat)}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              padding: '8px 10px',
              borderRadius: 8,
              border: 'none',
              background: checked ? 'rgba(0,0,0,0.04)' : 'transparent',
              cursor: blocked || !editable ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              fontSize: 13,
              lineHeight: '20px',
              color: '#000',
              opacity: blocked ? 0.45 : 1,
            }}
          >
            {cat}
          </button>
        )
      })}
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 内容语种 Capsule Tab + 编辑 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 38,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {value.supported_languages.map((code) => {
            const active = code === lang
            return (
              <button
                key={code}
                type="button"
                onClick={() => setActiveLang(code)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 12,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: '22px',
                  fontWeight: active ? 500 : 400,
                  color: active ? 'var(--color-brand-indigo)' : 'rgba(0,0,0,0.4)',
                  background: active ? 'rgba(99,102,241,0.1)' : 'transparent',
                }}
              >
                {contentLangLabel(code)}
              </button>
            )
          })}
        </div>
        {editable && (
          <SupportedLanguagesSelect
            value={value.supported_languages}
            default_language={value.default_language}
            onChange={onLangsChange}
          >
            <button type="button" style={iconGhost} aria-label="manage languages">
              <EditIcon width={18} height={18} />
            </button>
          </SupportedLanguagesSelect>
        )}
      </div>

      <DashSep />

      <DiField label={t('versionForm.supportedLanguages')} required>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {editable && (
            <SupportedLanguagesSelect
              value={value.supported_languages}
              default_language={value.default_language}
              onChange={onLangsChange}
            >
              <button type="button" style={iconBtn28} aria-label="add language">
                <PlusOutlined style={{ fontSize: 14 }} />
              </button>
            </SupportedLanguagesSelect>
          )}
          {value.supported_languages.map((code) => (
            <span key={code} style={chipStyle}>
              {contentLangLabel(code)}
              {editable && code !== value.default_language && (
                <button
                  type="button"
                  onClick={() => removeLang(code)}
                  style={chipClose}
                  aria-label="remove language"
                >
                  <CloseOutlined style={{ fontSize: 10 }} />
                </button>
              )}
            </span>
          ))}
        </div>
      </DiField>

      <DashSep />

      <DiField label={t('versionForm.appCategory')}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {editable && (
            <Dropdown
              open={catOpen}
              onOpenChange={setCatOpen}
              popupRender={() => catPanel}
              trigger={['click']}
              placement="bottomLeft"
            >
              <button type="button" style={iconBtn28} aria-label="add category">
                <PlusOutlined style={{ fontSize: 14 }} />
              </button>
            </Dropdown>
          )}
          {value.categories.map((cat) => (
            <span key={cat} style={chipStyle}>
              {cat}
              {editable && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...value,
                      categories: value.categories.filter((c) => c !== cat),
                    })
                  }
                  style={chipClose}
                  aria-label="remove category"
                >
                  <CloseOutlined style={{ fontSize: 10 }} />
                </button>
              )}
            </span>
          ))}
        </div>
      </DiField>

      <DashSep />

      <DiField label={t('versionForm.appTags')}>
        <AppTagsSelect
          value={value.tags}
          disabled={!editable}
          onChange={(tags) => onChange({ ...value, tags })}
        />
      </DiField>

      <DashSep />

      <DiField label={t('versionForm.oneSentence')}>
        <Input
          readOnly={!editable}
          placeholder={t('versionForm.oneSentencePlaceholder')}
          maxLength={80}
          value={loc.tagline}
          onChange={(e) => patchLoc({ tagline: e.target.value })}
          style={viewableInputStyle(editable)}
        />
      </DiField>

      <DashSep />

      <DiField label={t('versionForm.description')} required>
        <Input.TextArea
          readOnly={!editable}
          placeholder={t('versionForm.descriptionPlaceholder')}
          maxLength={5000}
          showCount
          rows={4}
          value={loc.description}
          onChange={(e) => patchLoc({ description: e.target.value })}
          style={viewableTextAreaStyle(editable)}
        />
      </DiField>

      {hasPc && (
        <>
          <DashSep />
          <DiField label={t('versionForm.pcScreenshots')}>
            <ScreenshotUploader
              kind="pc"
              value={loc.pc_screenshots}
              disabled={!editable}
              app_id={app_id}
              version_id={version_id}
              lang={lang}
              onChange={(next) => patchLoc({ pc_screenshots: next })}
            />
          </DiField>
        </>
      )}

      {hasMobile && (
        <>
          <DashSep />
          <DiField label={t('versionForm.mobileScreenshots')}>
            <ScreenshotUploader
              kind="mobile"
              value={loc.mobile_screenshots}
              disabled={!editable}
              app_id={app_id}
              version_id={version_id}
              lang={lang}
              onChange={(next) => patchLoc({ mobile_screenshots: next })}
            />
          </DiField>
        </>
      )}

      <DashSep />
      <UrlField
        label={t('versionForm.website')}
        editable={editable}
        value={value.website_url ?? ''}
        onChange={(website_url) => onChange({ ...value, website_url })}
      />

      <DashSep />
      <UrlField
        label={t('versionForm.privacyUrl')}
        editable={editable}
        value={value.privacy_policy_url ?? ''}
        onChange={(privacy_policy_url) => onChange({ ...value, privacy_policy_url })}
      />

      <DashSep />
      <UrlField
        label={t('versionForm.termsUrl')}
        editable={editable}
        value={value.terms_url ?? ''}
        onChange={(terms_url) => onChange({ ...value, terms_url })}
      />

      {SHOW_SUPPORT_ACCOUNT ? (
        <>
          <DashSep />
          <SupportAccountField
            app_id={app_id}
            editable={editable}
            value={value.support_account}
            onChange={(support_account) =>
              onChange({ ...valueRef.current, support_account })
            }
          />
        </>
      ) : null}
    </div>
  )
}

function DashSep() {
  return (
    <div
      style={{
        height: 1,
        borderBottom: '1px dashed var(--color-border-line)',
      }}
    />
  )
}

function UrlField({
  label,
  value,
  editable,
  onChange,
}: {
  label: string
  value: string
  editable: boolean
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()
  const [touched, setTouched] = useState(false)
  const invalid = !isOptionalHttpsUrl(value)
  const showError = touched && invalid
  return (
    <DiField label={label}>
      <Form.Item
        validateStatus={showError ? 'error' : undefined}
        help={showError ? t('versionForm.errUrl') : undefined}
        style={{ marginBottom: 0 }}
      >
        <Input
          readOnly={!editable}
          placeholder="https://"
          maxLength={DISPLAY_URL_MAX}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            const next = value.trim()
            if (next !== value) onChange(next)
            setTouched(true)
          }}
          style={viewableInputStyle(editable)}
        />
      </Form.Item>
    </DiField>
  )
}

function DiField({
  label,
  required,
  children,
}: {
  label: ReactNode
  required?: boolean
  children: ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 14,
          lineHeight: '22px',
          color: 'rgba(0,0,0,0.6)',
        }}
      >
        {label}
        {required && <span style={{ color: '#E53E3E' }}>*</span>}
      </div>
      {children}
    </div>
  )
}

const inputStyle: CSSProperties = {
  height: 50,
  borderRadius: 10,
  borderColor: 'var(--color-border-line)',
  background: 'var(--color-background-module-container)',
}

/** 已上线等只读态用 readOnly 而非 disabled，避免字色浅到像「没填」 */
function viewableInputStyle(editable: boolean): CSSProperties {
  return {
    ...inputStyle,
    color: 'rgba(0,0,0,0.88)',
    cursor: editable ? 'text' : 'default',
  }
}

function viewableTextAreaStyle(editable: boolean): CSSProperties {
  return {
    borderRadius: 10,
    borderColor: 'var(--color-border-line)',
    background: 'var(--color-background-module-container)',
    color: 'rgba(0,0,0,0.88)',
    cursor: editable ? 'text' : 'default',
  }
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  padding: '0 12px',
  borderRadius: 8,
  background: '#EFEFEF',
  fontSize: 13,
  lineHeight: '20px',
}

const chipClose: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  width: 14,
  height: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const iconBtn28: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid var(--color-border-line)',
  background: 'var(--color-background-module-container)',
  backdropFilter: 'blur(15px)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

const iconGhost: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#000',
}
